// Language and appearance, grouped: the `sidebar-map` tour's "Bahasa dan tampilan" step points at
// both at once (`data-tour="nav-language"`).
//
// Theme: the bar offers light and dark only, mapped to the APEX modes (revamp plan §7.2). A user on a
// Horizon mode keeps it: the toggle reads light/dark from `isDark` and only writes a mode when clicked,
// so their palette changes the first time they use it, never on its own. The seven modes collapse
// to two in the cleanup PR.
import { LanguagesIcon, MoonIcon, SunIcon } from 'lucide-react';

import { LANGUAGES } from '@constants/translations';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { HAPTIC, haptic, useHaptic } from '@utils/haptic';
import { Button } from '@/components/lumen/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/lumen/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { TopbarIconButton } from './TopbarIconButton';

function LanguageMenu() {
  const { language, setLanguage, t } = useLanguage();
  const label = t('language.switcher');
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={label}
              onClickCapture={() => haptic(HAPTIC.medium)}
              className="h-7 gap-1 px-1.5 text-fg-2 has-[>svg]:px-1.5 data-[state=open]:bg-tint data-[state=open]:text-fg"
            >
              <LanguagesIcon aria-hidden />
              <span className="font-mono text-xs uppercase">{language}</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={4}>
          {label}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuLabel className="text-fg-muted text-xs">{label}</DropdownMenuLabel>
        {/* Changing language persists to the profile (LanguageContext.setLanguage). */}
        <DropdownMenuRadioGroup value={language} onValueChange={(code) => setLanguage(code as typeof language)}>
          {LANGUAGES.map((lang) => (
            <DropdownMenuRadioItem key={lang.code} value={lang.code} className="gap-2.5">
              <span aria-hidden className="text-base leading-none">
                {lang.flag}
              </span>
              {lang.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The light/dark toggle on its own; the reviewer top bar uses it too. */
export function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const hapticMedium = useHaptic('medium');
  const label = isDark ? t('lumen.topbar.switchToLight') : t('lumen.topbar.switchToDark');
  return (
    <TopbarIconButton
      label={label}
      className="size-7"
      onClick={() => {
        hapticMedium();
        toggleTheme(isDark ? 'apex' : 'apex-night');
      }}
    >
      {isDark ? <SunIcon /> : <MoonIcon />}
    </TopbarIconButton>
  );
}

export function PreferencesGroup() {
  return (
    <div data-tour="nav-language" className="flex items-center gap-0.5 rounded-control border border-line p-0.5">
      <LanguageMenu />
      <ThemeToggle />
    </div>
  );
}
